import { createContext, useEffect, useState, useCallback, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { formatError, logError } from "../services/logging"
import { API_URL } from "../config/api"

const UserContext = createContext(null)

const TIMEOUT = 60 * 60 * 1000 // 1 hour auto logout
const TIMESTAMP_KEY = "loginTimeStamp"

const UserProvider = ({ children }) => {
    const [loggedInUser, setLoggedInUser] = useState(null)
    const location = useLocation()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const logOutTimerRef = useRef(null)
    const [logOutReason, setLogOutReason] = useState(null)

    const logInUser = (user) => {
      setLoggedInUser(user)
      localStorage.setItem(TIMESTAMP_KEY, Date.now())
    }

    const clearUserSession = useCallback((reason) => {
      setLoggedInUser(null)
      localStorage.removeItem(TIMESTAMP_KEY)
      setLogOutReason(reason)
      navigate("/")

      if (reason === "timeout") {
        toast.info("You've been logged out due to inactivity.", {
          onClose: () => setLogOutReason(null)
        })
      } else if (reason === "manual") {
        setLogOutReason(null)
      }
    }, [navigate])

    const logOutUser = useCallback(async (reason = "manual") => {
      try {
        const resp = await fetch(`${API_URL}/api/logout`, {
          method: "DELETE",
          credentials: "include",
        })
        
        if (!resp.ok) {
          const data = await resp.json()
          logError(data)
          toast.error(data.message || "Logout failed. Please try again.")
        }
      } catch (error) {
        if (reason === "timeout" || reason === "silent-timeout") {
          console.warn("Logout request failed during auto-logout (backend may be unavailable):", error.message)
        }
        logError(formatError(error))
        toast.error("An unexpected error occurred. Please try again.")
      } finally {
        clearUserSession(reason)
      }
    }, [navigate, clearUserSession])


    // check user in session on each page change (activity)
    useEffect(() => {
      let cancelled = false

      fetch(`${API_URL}/api/check_session`, {
        method: 'GET',
        credentials: 'include',
      })
      .then(resp => {
        if (!cancelled) {
          if (resp.ok) {
            resp.json().then(user => {
              setLoggedInUser(user)
              setLoading(false)
            })
          } else {
            setLoggedInUser(null)
            setLoading(false)
          }
        }
      })
      .catch(error => {
        if (!cancelled) {
          console.warn("Session check failed (backend may be unavailable):", error.message)
          setLoggedInUser(null)
          setLoading(false)
        }
      })
      return () => { cancelled = true }
      }, [location])

      // Auto log-out timer while in app, reset on activity
      useEffect(() => {
        if (!loggedInUser) return

        if (logOutTimerRef.current) {
          clearTimeout(logOutTimerRef.current)
        }

        const newTimer = setTimeout(() => {
          logOutUser("timeout")
        }, TIMEOUT)

        logOutTimerRef.current = newTimer

        return () => clearTimeout(newTimer)

      }, [location, loggedInUser, logOutUser])

      // Auto log-out time check if app is closed
      useEffect(() => {
        const loginTimeStamp = localStorage.getItem(TIMESTAMP_KEY)
        if (loginTimeStamp && Date.now() - loginTimeStamp > TIMEOUT) {
          logOutUser("silent-timeout")
        } else if (loggedInUser) {
          localStorage.setItem(TIMESTAMP_KEY, Date.now())
        }
      }, [location, loggedInUser, logOutUser])

  return (
    <UserContext.Provider value={{ 
      loggedInUser, 
      logInUser, 
      logOutUser, 
      logOutReason, 
      setLogOutReason, 
      loading 
    }}>
      {children}
    </UserContext.Provider>
  )
}

export { UserContext, UserProvider }